import { motion } from "framer-motion";
import {
  ArrowRight,
  Home,
  ChevronRight,
  Brain,
  Download,
  Zap,
  CreditCard,
  Users,
  Settings,
  MessageCircle,
  Shield,
  HelpCircle,
  FileText,
  Mail,
  User,
  Building,
  Globe,
  Star,
  BookOpen,
  Smartphone
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { useNavigateWithScroll } from "../../hooks/useNavigateWithScroll";

interface PageConfig {
  title: string;
  subtitle: string;
  description: string;
  primaryColor: string;
  secondaryColor: string;
  icon: any;
  badge?: string;
  backgroundPattern: 'dots' | 'waves' | 'gradient' | 'grid' | 'particles';
  cta?: {
    text: string;
    link: string;
    secondary?: {
      text: string;
      link: string;
    };
  };
}

interface UniversalHeroProps {
  pageName: string;
  breadcrumbs?: Array<{ name: string; href: string }>;
  customConfig?: Partial<PageConfig>;
}

export const UniversalHero: React.FC<UniversalHeroProps> = ({
  pageName,
  breadcrumbs = [],
  customConfig = {}
}) => {
  const { t } = useTranslation();
  const [particles, setParticles] = useState<Array<{ id: number; x: number; y: number; delay: number }>>([]);

  // Configuration par défaut pour chaque page
  const pageConfigs: Record<string, PageConfig> = {
    extension: {
      title: t('pages.extension.title'),
      subtitle: t('pages.extension.subtitle'),
      description: t('pages.extension.description'),
      primaryColor: 'from-blue-500 to-purple-600',
      secondaryColor: 'from-purple-500 to-pink-500',
      icon: Download,
      badge: 'pages.extension.badge',
      backgroundPattern: 'particles',
      cta: {
        text: t('pages.extension.cta.primary'),
        link: '#install',
        secondary: {
          text: t('pages.extension.cta.secondary'),
          link: '#features'
        }
      }
    },
    features: {
      title: t('pages.features.title'),
      subtitle: t('pages.features.subtitle'),
      description: t('pages.features.description'),
      primaryColor: 'from-green-500 to-blue-500',
      secondaryColor: 'from-blue-500 to-teal-500',
      icon: Zap,
      backgroundPattern: 'waves',
      cta: {
        text: t('pages.features.cta.primary'),
        link: '/extension',
        secondary: {
          text: t('pages.features.cta.secondary'),
          link: '/pricing'
        }
      }
    },
    pricing: {
      title: t('pages.pricing.title'),
      subtitle: t('pages.pricing.subtitle'),
      description: t('pages.pricing.description'),
      primaryColor: 'from-purple-500 to-pink-500',
      secondaryColor: 'from-pink-500 to-red-500',
      icon: CreditCard,
      backgroundPattern: 'gradient',
      cta: {
        text: t('pages.pricing.cta.primary'),
        link: '#plans',
        secondary: {
          text: t('pages.pricing.cta.secondary'),
          link: '/features'
        }
      }
    },
    about: {
      title: t('pages.about.title'),
      subtitle: t('pages.about.subtitle'),
      description: t('pages.about.description'),
      primaryColor: 'from-indigo-500 to-purple-500',
      secondaryColor: 'from-purple-500 to-blue-500',
      icon: Users,
      backgroundPattern: 'dots',
      cta: {
        text: t('pages.about.cta.primary'),
        link: '/contact',
        secondary: {
          text: t('pages.about.cta.secondary'),
          link: '/careers'
        }
      }
    },
    contact: {
      title: t('pages.contact.title'),
      subtitle: t('pages.contact.subtitle'),
      description: t('pages.contact.description'),
      primaryColor: 'from-teal-500 to-green-500',
      secondaryColor: 'from-green-500 to-blue-500',
      icon: Mail,
      backgroundPattern: 'grid',
      cta: {
        text: t('pages.contact.cta.primary'),
        link: '#contact-form',
        secondary: {
          text: t('pages.contact.cta.secondary'),
          link: '/support'
        }
      }
    },
    support: {
      title: t('pages.support.title'),
      subtitle: t('pages.support.subtitle'),
      description: t('pages.support.description'),
      primaryColor: 'from-orange-500 to-red-500',
      secondaryColor: 'from-red-500 to-pink-500',
      icon: HelpCircle,
      backgroundPattern: 'waves',
      cta: {
        text: t('pages.support.cta.primary'),
        link: '#faq',
        secondary: {
          text: t('pages.support.cta.secondary'),
          link: '/contact'
        }
      }
    },
    blog: {
      title: t('pages.blog.title'),
      subtitle: t('pages.blog.subtitle'),
      description: t('pages.blog.description'),
      primaryColor: 'from-cyan-500 to-blue-500',
      secondaryColor: 'from-blue-500 to-indigo-500',
      icon: BookOpen,
      backgroundPattern: 'dots',
      cta: {
        text: t('pages.blog.cta.primary'),
        link: '#latest-posts',
        secondary: {
          text: t('pages.blog.cta.secondary'),
          link: '/newsletter'
        }
      }
    },
    privacy: {
      title: t('pages.privacy.title'),
      subtitle: t('pages.privacy.subtitle'),
      description: t('pages.privacy.description'),
      primaryColor: 'from-gray-600 to-gray-800',
      secondaryColor: 'from-gray-500 to-gray-700',
      icon: Shield,
      backgroundPattern: 'grid'
    },
    terms: {
      title: t('pages.terms.title'),
      subtitle: t('pages.terms.subtitle'),
      description: t('pages.terms.description'),
      primaryColor: 'from-slate-600 to-slate-800',
      secondaryColor: 'from-slate-500 to-slate-700',
      icon: FileText,
      backgroundPattern: 'dots'
    },
    dashboard: {
      title: t('pages.dashboard.title'),
      subtitle: t('pages.dashboard.subtitle'),
      description: t('pages.dashboard.description'),
      primaryColor: 'from-violet-500 to-purple-600',
      secondaryColor: 'from-purple-500 to-indigo-500',
      icon: Brain,
      badge: 'pages.dashboard.badge',
      backgroundPattern: 'particles'
    }
  };

  // Configuration finale (fusion avec customConfig)
  const config = {
    ...pageConfigs[pageName] || {
      title: t('pages.default.title'),
      subtitle: t('pages.default.subtitle'),
      description: t('pages.default.description'),
      primaryColor: 'from-blue-500 to-purple-600',
      secondaryColor: 'from-purple-500 to-pink-500',
      icon: Globe,
      backgroundPattern: 'gradient' as const
    },
    ...customConfig
  };

  // Génération des particules pour l'animation
  useEffect(() => {
    if (config.backgroundPattern === 'particles') {
      const newParticles = Array.from({ length: 15 }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        delay: Math.random() * 2
      }));
      setParticles(newParticles);
    }
  }, [config.backgroundPattern]);

  const renderBackgroundPattern = () => {
    switch (config.backgroundPattern) {
      case 'dots':
        return (
          <div className="absolute inset-0 opacity-20">
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle, currentColor 1px, transparent 1px)',
              backgroundSize: '30px 30px'
            }} />
          </div>
        );

      case 'waves':
        return (
          <div className="absolute inset-0 opacity-30">
            <svg className="absolute bottom-0 left-0 w-full h-full" viewBox="0 0 1200 120" preserveAspectRatio="none">
              <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.32,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" fill="currentColor" />
            </svg>
          </div>
        );

      case 'grid':
        return (
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: 'linear-gradient(currentColor 1px, transparent 1px), linear-gradient(90deg, currentColor 1px, transparent 1px)',
              backgroundSize: '50px 50px'
            }} />
          </div>
        );

      case 'particles':
        return (
          <div className="absolute inset-0 overflow-hidden">
            {particles.map((particle) => (
              <motion.div
                key={particle.id}
                className="absolute w-2 h-2 bg-white/20 rounded-full"
                style={{
                  left: `${particle.x}%`,
                  top: `${particle.y}%`,
                }}
                animate={{
                  y: [0, -20, 0],
                  opacity: [0.2, 0.5, 0.2],
                  scale: [1, 1.2, 1]
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  delay: particle.delay,
                  ease: "easeInOut"
                }}
              />
            ))}
          </div>
        );

      default:
        return null;
    }
  };

  const IconComponent = config.icon;
  const navigate = useNavigateWithScroll()

  return (
    <section className={`relative py-20 lg:py-32 overflow-hidden bg-gradient-to-br ${config.primaryColor} text-white`}>
      {/* Background Pattern */}
      {renderBackgroundPattern()}

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/20 via-transparent to-black/20" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumbs */}
        {breadcrumbs.length > 0 && (
          <motion.nav
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex items-center space-x-2 text-white/80 text-sm mb-8"
          >
            <Home className="w-4 h-4" />
            <button onClick={()=>{navigate('/')}} className="hover:text-white transition-colors">
              {t('breadcrumbs.home')}
            </button>
            {breadcrumbs.map((crumb, index) => (
              <div key={index} className="flex items-center space-x-2">
                <ChevronRight className="w-4 h-4" />
                <button
                  // href={crumb.href}
                  onClick={()=>{navigate(crumb.href)}}
                  className={`hover:text-white transition-colors ${index === breadcrumbs.length - 1 ? 'text-white font-medium' : ''
                    }`}
                >
                  {crumb.name}
                </button>
              </div>
            ))}
          </motion.nav>
        )}

        <div className="flex flex-col lg:flex-row items-center justify-between">
          {/* Contenu Principal */}
          <div className="flex-1 text-center lg:text-left mb-12 lg:mb-0">
            {/* Badge */}
            {config.badge && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6 }}
                className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium mb-6"
              >
                <Star className="w-4 h-4" />
                <span>{config.badge}</span>
              </motion.div>
            )}

            {/* Titre */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="text-4xl lg:text-6xl font-bold mb-6"
            >
              {config.title}
            </motion.h1>

            {/* Sous-titre */}
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl lg:text-2xl font-medium text-white/90 mb-4"
            >
              {config.subtitle}
            </motion.p>

            {/* Description */}
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="text-lg text-white/80 max-w-2xl mb-8"
            >
              {config.description}
            </motion.p>

            {/* CTAs */}
            {/* {config.cta && (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-4"
              >
                <motion.a
                  href={config.cta.link}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="inline-flex items-center space-x-2 bg-white text-gray-900 px-8 py-4 rounded-2xl font-semibold shadow-lg hover:shadow-xl transition-all"
                >
                  <span>{config.cta.text}</span>
                  <ArrowRight className="w-5 h-5" />
                </motion.a>

                {config.cta.secondary && (
                  <motion.a
                    href={config.cta.secondary.link}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="inline-flex items-center space-x-2 border-2 border-white/30 text-white px-8 py-4 rounded-2xl font-semibold hover:bg-white/10 transition-all"
                  >
                    <span>{config.cta.secondary.text}</span>
                  </motion.a>
                )}
              </motion.div>
            )} */}
          </div>

          {/* Icône Flottante */}
          <motion.div
            initial={{ opacity: 0, scale: 0.5, rotate: -180 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="lg:flex-shrink-0 lg:ml-12"
          >
            <motion.div
              animate={{
                y: [0, -10, 0],
                rotate: [0, 5, -5, 0]
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className={`w-32 h-32 lg:w-40 lg:h-40 bg-gradient-to-br ${config.secondaryColor} rounded-3xl flex items-center justify-center shadow-2xl`}
            >
              <IconComponent className="w-16 h-16 lg:w-20 lg:h-20 text-white" />
            </motion.div>

            {/* Effets décoratifs autour de l'icône */}
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              className="absolute -inset-4 border-2 border-white/20 rounded-full opacity-30"
            />
            <motion.div
              animate={{ rotate: -360 }}
              transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
              className="absolute -inset-8 border border-white/10 rounded-full opacity-20"
            />
          </motion.div>
        </div>

        {/* Indicateur de scroll */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1 }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center"
          >
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
              className="w-1 h-3 bg-white/50 rounded-full mt-2"
            />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};